/**
 * Query parameters that may be forwarded to analytics.
 *
 * This is an allowlist rather than a denylist on purpose. Analytics receives a
 * page path on every navigation, and the app's URLs carry things that must not
 * reach a third party: an email verification link holds `token` and `email`, and
 * the guest plan builder puts `goal` and `diet` in the query. A dietary
 * requirement can reveal a health condition or a religious belief, and a weight
 * goal is data concerning health, so neither belongs in an advertising export.
 *
 * With an allowlist, a query parameter added later is excluded until someone
 * decides it is safe, which is the right default for this direction of travel.
 */
const SAFE_QUERY_KEYS = new Set([
  // Campaign attribution.
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'wbraid', 'gbraid', 'fbclid', 'msclkid',
  // Product navigation that describes the journey, not the person.
  'source', 'src', 'flow', 'from_recipe', 'from_planner', 'fromMealPlan',
  'checkout_status', 'checkout_provider', 'plan', 'notice', 'lang',
  'new', 'newUser',
])

/**
 * A page path safe to send to analytics: the route, plus only those query
 * parameters known to carry no personal or sensitive detail. The fragment is
 * always dropped, since nothing needs it and it can hold anything.
 *
 * @param {string} fullPath a Vue Router `to.fullPath`
 * @returns {string}
 */
export function analyticsPagePath (fullPath) {
  if (typeof fullPath !== 'string' || fullPath === '') return fullPath

  try {
    const url = new URL(fullPath, 'https://placeholder.invalid')
    const kept = new URLSearchParams()
    for (const [key, value] of url.searchParams.entries()) {
      if (SAFE_QUERY_KEYS.has(key)) kept.append(key, value)
    }
    const query = kept.toString()
    return url.pathname + (query ? `?${query}` : '')
  } catch {
    // Unparseable, so drop everything after the path rather than forward it.
    const cut = fullPath.search(/[?#]/)
    return cut === -1 ? fullPath : fullPath.slice(0, cut)
  }
}

export const __SAFE_QUERY_KEYS = SAFE_QUERY_KEYS
